import type { AgentExecutionDto } from '@terra/shared-contracts-agent-execution';
import type { AiDiscoveryRunHistoryEntry } from '@terra/shared-contracts-ai-applications';
import {
  type AiDiscoveryStatusBarCounts,
  type AiDiscoveryStatusBarFilter,
} from './ai-discovery-run-status-bar.util';
import {
  formatDiscoveryDuration,
  getAiDiscoveryMetadata,
  getExecutionLabel,
} from './discovery.utils';
import {
  aggregateMcpDiscoveryRuns,
  buildMcpDiscoveryRun,
  isMcpToolDiscoveryExecution,
  resolveMcpDiscoveryRunStatus,
  sortDiscoveryExecutionsByTime,
  sortMcpDiscoveryRunsByTime,
  sortMcpDiscoveryToolExecutionsForDisplay,
} from './mcp-discovery-run-aggregate.util';
import type { McpDiscoveryRun } from './mcp-discovery-run.types';

export type { McpDiscoveryRun } from './mcp-discovery-run.types';
export {
  aggregateMcpDiscoveryRuns,
  isMcpToolDiscoveryExecution,
} from './mcp-discovery-run-aggregate.util';

export const MCP_DISCOVERY_RERUN_ID_SUFFIX = '-rerun';

export function resolveSourceDiscoveryRunId(run: McpDiscoveryRun): string {
  if (run.isDiscoveryRerun && run.id.endsWith(MCP_DISCOVERY_RERUN_ID_SUFFIX)) {
    return run.id.slice(0, -MCP_DISCOVERY_RERUN_ID_SUFFIX.length);
  }

  return run.id;
}

export function resolveDiscoveryRunHistoryStatusBarBucket(
  status: AgentExecutionDto['status'] | AiDiscoveryRunHistoryEntry['status'],
): AiDiscoveryStatusBarFilter {
  if (status === 'PENDING' || status === 'RUNNING') {
    return 'IN_PROGRESS';
  }

  if (status === 'FAILED' || status === 'BLOCKED') {
    return 'FAILED';
  }

  if (status === 'COMPLETED') {
    return 'COMPLETED';
  }

  return 'STOPPED';
}

export function mcpDiscoveryRunFromHistoryEntry(
  entry: AiDiscoveryRunHistoryEntry,
): McpDiscoveryRun {
  const durationMs =
    entry.completedAt != null
      ? new Date(entry.completedAt).getTime() -
        new Date(entry.startedAt).getTime()
      : null;
  const status = resolveMcpDiscoveryRunStatus({
    status: entry.status,
    verifiedProbeCount: entry.verifiedProbeCount,
    failedProbeCount: entry.failedProbeCount,
  });

  return {
    id: entry.id,
    startedAt: entry.startedAt,
    completedAt: entry.completedAt,
    durationMs,
    status,
    statusBarBucket: resolveDiscoveryRunHistoryStatusBarBucket(status),
    probeCount: entry.probeCount,
    verifiedProbeCount: entry.verifiedProbeCount,
    failedProbeCount: entry.failedProbeCount,
    representativeExecutionId: entry.id,
    executions: [],
    failureReason: entry.failureReason ?? undefined,
  };
}

export function mcpDiscoveryRunsFromSummaryHistory(
  historyEntries: AiDiscoveryRunHistoryEntry[],
): McpDiscoveryRun[] {
  return sortMcpDiscoveryRunsByTime(
    historyEntries.map(mcpDiscoveryRunFromHistoryEntry),
  );
}

function isFailedToolDiscoveryExecution(execution: AgentExecutionDto): boolean {
  const metadata = getAiDiscoveryMetadata(execution.metadata);
  const status = execution.status;

  return (
    metadata?.verdictStatus === 'FAILED' ||
    status === 'FAILED' ||
    status === 'BLOCKED'
  );
}

function partitionExecutionsForDiscoveryRerun({
  historyRun,
  executions,
}: {
  historyRun: McpDiscoveryRun;
  executions: AgentExecutionDto[];
}): {
  originalExecutions: AgentExecutionDto[];
  rerunExecutions: AgentExecutionDto[];
} {
  const sortedExecutions = sortDiscoveryExecutionsByTime(executions);
  const originalExecutions: AgentExecutionDto[] = [];
  const rerunExecutions: AgentExecutionDto[] = [];
  const failedToolCompletedAtMs = new Map<string, number>();
  const historyCompletedAtMs =
    historyRun.completedAt != null &&
    (historyRun.status === 'COMPLETED' || historyRun.status === 'FAILED')
      ? new Date(historyRun.completedAt).getTime()
      : null;

  for (const execution of sortedExecutions) {
    const metadata = getAiDiscoveryMetadata(execution.metadata);
    const toolName = metadata?.mcpToolName ?? null;
    const createdAtMs = new Date(execution.createdAt).getTime();
    const failedToolCompletedAt =
      toolName != null ? failedToolCompletedAtMs.get(toolName) : undefined;
    const isRetryAfterFailedTool =
      failedToolCompletedAt != null && createdAtMs > failedToolCompletedAt;
    const isRetryAfterTerminalHistory =
      historyCompletedAtMs != null && createdAtMs > historyCompletedAtMs;

    if (isRetryAfterFailedTool || isRetryAfterTerminalHistory) {
      rerunExecutions.push(execution);
      continue;
    }

    originalExecutions.push(execution);

    if (
      toolName != null &&
      execution.completedAt != null &&
      isFailedToolDiscoveryExecution(execution)
    ) {
      failedToolCompletedAtMs.set(
        toolName,
        new Date(execution.completedAt).getTime(),
      );
    }
  }

  return { originalExecutions, rerunExecutions };
}

function resolveIsDiscoveryRerunOrphan({
  run,
  historyRuns,
}: {
  run: McpDiscoveryRun;
  historyRuns: McpDiscoveryRun[];
}): boolean {
  const historyIds = new Set(historyRuns.map((historyRun) => historyRun.id));

  if (historyIds.has(run.id)) {
    return false;
  }

  const linkedDiscoveryRunIds = new Set(
    run.executions.flatMap((execution) => {
      const discoveryRunId = getAiDiscoveryMetadata(
        execution.metadata,
      )?.discoveryRunId;

      return typeof discoveryRunId === 'string' && discoveryRunId.length > 0
        ? [discoveryRunId]
        : [];
    }),
  );

  if (linkedDiscoveryRunIds.size > 0) {
    return historyRuns.some((historyRun) =>
      linkedDiscoveryRunIds.has(historyRun.id),
    );
  }

  const latestCompletedHistory = historyRuns
    .filter((historyRun) => historyRun.completedAt != null)
    .sort(
      (left, right) =>
        new Date(right.completedAt ?? 0).getTime() -
        new Date(left.completedAt ?? 0).getTime(),
    )[0];

  if (!latestCompletedHistory?.completedAt) {
    return false;
  }

  return (
    new Date(run.startedAt).getTime() >
    new Date(latestCompletedHistory.completedAt).getTime()
  );
}

export function mergeMcpDiscoveryRunsPreferringHistory({
  historyRuns,
  executionRuns,
}: {
  historyRuns: McpDiscoveryRun[];
  executionRuns: McpDiscoveryRun[];
}): McpDiscoveryRun[] {
  const executionById = new Map(executionRuns.map((run) => [run.id, run]));
  const historyIds = new Set(historyRuns.map((run) => run.id));

  const mergedHistoryRuns = historyRuns.flatMap((historyRun) => {
    const executionRun = executionById.get(historyRun.id);

    if (!executionRun || executionRun.executions.length === 0) {
      return [historyRun];
    }

    const { originalExecutions, rerunExecutions } =
      partitionExecutionsForDiscoveryRerun({
        historyRun,
        executions: executionRun.executions,
      });

    if (rerunExecutions.length === 0) {
      return [
        {
          ...executionRun,
          failureReason: historyRun.failureReason,
        },
      ];
    }

    const originalRun =
      originalExecutions.length > 0
        ? buildMcpDiscoveryRun({
            id: historyRun.id,
            executions: originalExecutions,
          })
        : historyRun;

    const rerunRun = {
      ...buildMcpDiscoveryRun({
        id: `${historyRun.id}${MCP_DISCOVERY_RERUN_ID_SUFFIX}`,
        executions: rerunExecutions,
      }),
      isDiscoveryRerun: true,
    };

    return [
      {
        ...originalRun,
        failureReason: historyRun.failureReason,
        status:
          originalExecutions.length > 0
            ? originalRun.status
            : historyRun.status,
        statusBarBucket:
          originalExecutions.length > 0
            ? originalRun.statusBarBucket
            : historyRun.statusBarBucket,
      },
      rerunRun,
    ];
  });

  const orphanedExecutionRuns = executionRuns
    .filter((run) => !historyIds.has(run.id))
    .map((run) => ({
      ...run,
      isDiscoveryRerun: resolveIsDiscoveryRerunOrphan({
        run,
        historyRuns,
      }),
    }));

  return sortMcpDiscoveryRunsByTime([
    ...mergedHistoryRuns,
    ...orphanedExecutionRuns,
  ]);
}

export function resolveMcpDiscoveryRuns({
  historyEntries,
  executions,
}: {
  historyEntries: AiDiscoveryRunHistoryEntry[];
  executions: AgentExecutionDto[];
}): McpDiscoveryRun[] {
  return mergeMcpDiscoveryRunsPreferringHistory({
    historyRuns: mcpDiscoveryRunsFromSummaryHistory(historyEntries),
    executionRuns: aggregateMcpDiscoveryRuns(executions),
  });
}

export function attachExecutionsToMcpDiscoveryRun({
  run,
  executions,
}: {
  run: McpDiscoveryRun;
  executions: AgentExecutionDto[];
}): McpDiscoveryRun {
  const sourceDiscoveryRunId = resolveSourceDiscoveryRunId(run);
  const relatedExecutions = executions.filter((execution) => {
    const discoveryRunId = getAiDiscoveryMetadata(
      execution.metadata,
    )?.discoveryRunId;

    return discoveryRunId === sourceDiscoveryRunId;
  });

  if (relatedExecutions.length === 0) {
    return run;
  }

  const matchedExecutions = run.isDiscoveryRerun
    ? partitionExecutionsForDiscoveryRerun({
        historyRun: {
          ...run,
          id: sourceDiscoveryRunId,
          completedAt: new Date(
            new Date(run.startedAt).getTime() - 1,
          ).toISOString(),
          status: 'COMPLETED',
        },
        executions: relatedExecutions,
      }).rerunExecutions
    : relatedExecutions;

  if (matchedExecutions.length === 0) {
    return run;
  }

  const executionsById = new Map<string, AgentExecutionDto>();

  for (const execution of run.executions) {
    executionsById.set(execution.id, execution);
  }

  for (const execution of matchedExecutions) {
    executionsById.set(execution.id, execution);
  }

  const runExecutions = sortDiscoveryExecutionsByTime([
    ...executionsById.values(),
  ]);

  const representativeExecution =
    runExecutions.find((execution) => {
      const status = execution.status;

      return status === 'RUNNING' || status === 'PENDING';
    }) ??
    runExecutions.at(-1) ??
    null;

  const rebuilt = buildMcpDiscoveryRun({
    id: run.id,
    executions: runExecutions,
  });

  return {
    ...rebuilt,
    isDiscoveryRerun: run.isDiscoveryRerun,
    failureReason: run.failureReason,
    representativeExecutionId:
      representativeExecution?.id ?? rebuilt.representativeExecutionId,
  };
}

export function computeMcpDiscoveryStatusBarCounts(
  runs: McpDiscoveryRun[],
): AiDiscoveryStatusBarCounts {
  return runs.reduce<AiDiscoveryStatusBarCounts>(
    (counts, run) => {
      counts[run.statusBarBucket] += 1;

      return counts;
    },
    {
      IN_PROGRESS: 0,
      COMPLETED: 0,
      FAILED: 0,
      STOPPED: 0,
    },
  );
}

export function filterMcpDiscoveryRuns({
  runs,
  search,
  selectedStatuses,
  statusBarFilter,
}: {
  runs: McpDiscoveryRun[];
  search: string;
  selectedStatuses: string[];
  statusBarFilter: AiDiscoveryStatusBarFilter | undefined;
}): McpDiscoveryRun[] {
  const normalizedSearch = search.trim().toLowerCase();

  return runs.filter((run) => {
    const filterStatus = run.status;
    const matchesStatusBar =
      statusBarFilter == null || run.statusBarBucket === statusBarFilter;
    const matchesStatus =
      selectedStatuses.length === 0 || selectedStatuses.includes(filterStatus);
    const matchesSearch =
      !normalizedSearch ||
      formatDiscoveryDuration({
        startedAt: run.startedAt,
        completedAt: run.completedAt,
        durationMs: run.durationMs,
      })
        .toLowerCase()
        .includes(normalizedSearch) ||
      `${run.verifiedProbeCount}/${run.probeCount}`.includes(
        normalizedSearch,
      ) ||
      run.executions.some((execution) =>
        getExecutionLabel({ execution })
          .toLowerCase()
          .includes(normalizedSearch),
      );

    return matchesStatusBar && matchesStatus && matchesSearch;
  });
}

export function partitionMcpRunExecutions(executions: AgentExecutionDto[]): {
  serverExecutions: AgentExecutionDto[];
  toolExecutions: AgentExecutionDto[];
} {
  const serverExecutions: AgentExecutionDto[] = [];
  const toolExecutions: AgentExecutionDto[] = [];

  for (const execution of executions) {
    if (isMcpToolDiscoveryExecution(execution)) {
      toolExecutions.push(execution);
      continue;
    }

    serverExecutions.push(execution);
  }

  return { serverExecutions, toolExecutions };
}

export function resolveMcpRunTopTraceExecution(
  run: McpDiscoveryRun,
): AgentExecutionDto | null {
  const mcpServerExecution = run.executions.find((execution) => {
    return (
      getAiDiscoveryMetadata(execution.metadata)?.componentType === 'MCP_SERVER'
    );
  });

  if (mcpServerExecution) {
    return mcpServerExecution;
  }

  const { serverExecutions } = partitionMcpRunExecutions(run.executions);

  if (serverExecutions.length > 0) {
    return serverExecutions[0] ?? null;
  }

  return null;
}

export function resolveMcpRunToolListExecutions({
  run,
  topTraceExecution,
}: {
  run: McpDiscoveryRun;
  topTraceExecution: AgentExecutionDto | null;
}): AgentExecutionDto[] {
  const { toolExecutions } = partitionMcpRunExecutions(run.executions);
  const visibleToolExecutions =
    topTraceExecution == null
      ? toolExecutions
      : toolExecutions.filter(
          (execution) => execution.id !== topTraceExecution.id,
        );

  return sortMcpDiscoveryToolExecutionsForDisplay(visibleToolExecutions);
}
